import React, { useEffect, useState } from 'react';
import { Divider, Input, Switch } from "@nextui-org/react";
import { FormState, useCreateApiKeyMutation } from '@circuitry-ai/doc-data';
import { textCapitalize, checkType, CtyButton, CtyShowToast, CtySpinner, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react';

interface FormValues {
    api_name: string;
    expiration_days: number;
    domain_name: string;
}

interface APIKeyConfigurationProps {
    refetchApiKeysList: () => void;
}

const ApiKeyConfiguration: React.FC<APIKeyConfigurationProps> = ({ refetchApiKeysList }) => {
    const { t } = useTranslation();
    const hasApiKeysAccess = useHasApplicationResource('api_keys');
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const apiConnectionDetails = location?.state?.apiKeyDetails;
    const formState = params.get('state');

    const [apiKeyValue, setApiKeyValue] = useState<string>(apiConnectionDetails?.api_key_value);
    const [createApiKey, { isLoading: isCreateAPiKeyLoading }] = useCreateApiKeyMutation();
    const isFormReadOnly = formState === FormState.UPDATE;

    const { register, handleSubmit, formState: { errors }, reset } = useForm<FormValues>({ defaultValues: { api_name: '', expiration_days: 0, domain_name: '' } });

    useEffect(() => {
        if (formState === FormState.UPDATE && apiConnectionDetails) {
            reset({
                api_name: apiConnectionDetails.api_name,
                expiration_days: apiConnectionDetails.expiration_days,
                domain_name: apiConnectionDetails.domain_name,
            });
        }
    }, [formState, apiConnectionDetails, reset]);

    const onSubmit = async (data: FormValues) => {
        try {
            const payload = {
                api_name: data.api_name,
                expiration_days: +data.expiration_days,
                domain_name: data.domain_name,
            };
            const response = await createApiKey(payload).unwrap();
            refetchApiKeysList();
            setApiKeyValue(response?.data?.api_key_value)
            CtyShowToast(checkType.success, response?.message);
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to update ApiKey details')}: ${(error as Error)?.message || 'Something went wrong!'}`)
        }
    };

    function navigateToApiKeysList() {
        navigate('/settings/apikey');
    }

    function renderFooterButtons() {
        return (
            <div className='flex flex-row gap-3'>
                <CtyButton variant="light" size="lg" radius="sm" onPress={navigateToApiKeysList} className="border-1 p-5 text-foreground border-default-300">
                    Back
                </CtyButton>
                {!apiKeyValue &&
                    <CtyButton variant="light" size="lg" radius="sm" onPress={handleSubmit(onSubmit)} className="border-1 p-5 text-foreground border-default-300" isDisabled={!hasApiKeysAccess}>
                        Generate Secret Key
                    </CtyButton>
                }
            </div>
        )
    }

    function renderProfileContent() {
        return (
            <div className='flex flex-col'>
                <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-1 my-3">
                        <div className="font-poppins text-lg font-semibold leading-7 text-left">{t(`${isFormReadOnly ? textCapitalize(apiConnectionDetails?.api_name) : 'Create'} API`)}</div>
                        <p className="font-poppins text-sm font-normal leading-5 text-left text-default-600">{t('Set up an API configuration and generate a token for your Advisor.')}</p>
                    </div>
                    {!isFormReadOnly && <Switch isSelected={!isFormReadOnly} size="sm" onValueChange={navigateToApiKeysList} aria-label="Automatic updates">Create</Switch>}
                </div>
                <div className={`w-[80%] px-1 flex flex-col gap-5`}>
                    <Divider />
                    <div className={`flex flex-col gap-5 `}>
                        <div className='flex gap-5'>
                            <label htmlFor="connection_name" className="w-[12%]  text-sm">
                                API Name<span className="text-danger">*</span>
                            </label>
                            <div className="flex-1 mt-1">
                                <Input
                                    id="api_name"
                                    label="Title"
                                    labelPlacement={'outside'}
                                    readOnly={isFormReadOnly}
                                    isInvalid={!!errors.api_name}
                                    placeholder='Dashboard'
                                    errorMessage={errors.api_name?.message}
                                    {...register('api_name', {
                                        required: 'Api name is required'
                                    })}
                                />
                            </div>
                        </div>

                        {isFormReadOnly && <div className="flex items-start gap-5">
                            <label htmlFor="expiration_days" className="w-[12%]  text-sm">
                                Expirations On<span className="text-danger">*</span>
                            </label>
                            <div className="relative flex-1 w-[12%]">
                                <Input
                                    id="expiration_days"
                                    type="date"
                                    label="Date"
                                    labelPlacement={'outside'}
                                    readOnly={isFormReadOnly}
                                    value={new Date(apiConnectionDetails?.expiration_date).toISOString().split('T')[0]}
                                />
                            </div>
                        </div>}

                        {!isFormReadOnly && <div className="flex items-start gap-5">
                            <label htmlFor="expiration_days" className="w-[12%]  text-sm">
                                Expiration days<span className="text-danger">*</span>
                            </label>
                            <div className="relative mt-1 ">
                                <Input
                                    id="expiration_days"
                                    type="number"
                                    label="Number of Active Days"
                                    labelPlacement={'outside'}
                                    placeholder='0'
                                    isInvalid={!!errors.expiration_days}
                                    errorMessage={errors.expiration_days?.message}
                                    {...register('expiration_days', {
                                        required: 'Expiration days is required',
                                        min: {
                                            value: 1,
                                            message: 'Expiration days must be greater than 1'
                                        }
                                    })}
                                />
                            </div>
                        </div>}

                        <div className='flex gap-5'>
                            <label htmlFor="domain_name" className="w-[12%]  text-sm">
                                Domain Name<span className="text-danger">*</span>
                            </label>
                            <div className="flex-1 mt-1">
                                <Input
                                    id="domain_name"
                                    label="URL starting with HTTPS://"
                                    labelPlacement={'outside'}
                                    placeholder='https://www.loremipsum.com'
                                    isInvalid={!!errors.domain_name}
                                    readOnly={isFormReadOnly}
                                    errorMessage={errors.domain_name?.message}
                                    {...register('domain_name', {
                                        required: 'Domain Name is required',
                                    })}
                                />
                            </div>
                        </div>

                        {apiKeyValue && <div className='flex gap-5'>
                            <label htmlFor="secret_key" className="w-[12%] text-sm">
                                Secret Key<span className="text-danger">*</span>
                            </label>
                            <div className="flex-1">
                                <Input
                                    id="secret_key"
                                    value={apiKeyValue}
                                    label="Token"
                                    labelPlacement={'outside'}
                                    readOnly={isFormReadOnly}
                                    endContent={
                                        <CtyButton
                                            variant="light"
                                            onPress={() => navigator.clipboard.writeText(apiKeyValue)}
                                            startContent={<Icon icon="hugeicons:copy-01" className="text-foreground" height={20} width={20} />}
                                            className='border-l rounded-none'
                                        >
                                            Copy
                                        </CtyButton>
                                    }
                                />
                            </div>
                        </div>}
                    </div>
                    <Divider />
                    <div className='flex'>
                        {isCreateAPiKeyLoading ? <div>
                            <CtySpinner size="md" />
                            <div className='text-sm'>Creating Key</div>
                        </div> : renderFooterButtons()}
                    </div>
                </div>
            </div>
        )
    }


    return (
        <div className="pr-3 flex flex-col h-full">
            {renderProfileContent()}
        </div>
    )
}


export default ApiKeyConfiguration;